import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ApiMeta } from '../../_core/_model/api-response';
import type { CurrencyDetailsDto } from '../../_core/_model/currency';
import {
  CreateHouseCurrencyPairDto,
  CreateHouseLiquidityAdjustmentDto,
  HouseCurrencyPairDto,
  HouseCurrencyPairForm,
  HouseGatewayCustomerProfileDto,
  HouseInstantTradeAdminDto,
  HouseLedgerEntryDto,
  HouseLiquidityAdjustmentDto,
  HouseLiquidityAlertDto,
  HouseManagementTab,
  HouseOrderAdminDto,
  HouseOverviewDto,
  HouseRateProviderOptionDto,
  HouseReconciliationRunDto,
  HouseWalletSummaryDto,
  HouseWithdrawalQuoteResponse,
  PayoutFieldRequirementDto,
  UpdateHouseCurrencyPairDto,
  WithdrawalDto,
  WithdrawalFormConfigResponseDto
} from '../../_core/_model/house-management';
import { UserRole } from '../../_core/_model/user-role';
import { ApiService } from '../../_core/_services/api.service';
import { AuthService } from '../../_core/_services/auth.service';
import { HouseManagementService } from '../../_core/_services/house-management.service';
import { NotificationService } from '../../_core/_services/notification.service';

interface HouseCurrencyOption {
  code: string;
  name: string;
  symbol: string;
}

@Component({
  selector: 'app-admin-house-management',
  standalone: false,
  templateUrl: './admin-house-management.html',
  styleUrl: './admin-house-management.css'
})
export class AdminHouseManagement implements OnInit, OnDestroy {
  readonly tabs: Array<{ key: HouseManagementTab; label: string }> = [
    { key: 'overview', label: 'Overview' },
    { key: 'liquidity', label: 'Liquidity' },
    { key: 'pairs', label: 'Currency Pairs' },
    { key: 'trades', label: 'Instant Trades' },
    { key: 'orders', label: 'House Orders' },
    { key: 'operations', label: 'Operations' },
    { key: 'withdrawals', label: 'Withdrawals' }
  ];

  activeTab: HouseManagementTab = 'overview';
  currentRole: UserRole | null = null;
  private readonly sub = new Subscription();

  overview: HouseOverviewDto | null = null;
  wallets: HouseWalletSummaryDto[] = [];
  pairs: HouseCurrencyPairDto[] = [];
  rateProviders: HouseRateProviderOptionDto[] = [];
  availableCurrencies: HouseCurrencyOption[] = [];
  adjustments: HouseLiquidityAdjustmentDto[] = [];
  ledgerEntries: HouseLedgerEntryDto[] = [];
  trades: HouseInstantTradeAdminDto[] = [];
  orders: HouseOrderAdminDto[] = [];
  alerts: HouseLiquidityAlertDto[] = [];
  reconciliationRuns: HouseReconciliationRunDto[] = [];
  selectedReconciliationRun: HouseReconciliationRunDto | null = null;
  withdrawals: WithdrawalDto[] = [];
  gatewayProfiles: HouseGatewayCustomerProfileDto[] = [];

  overviewLoading = false;
  walletsLoading = false;
  pairsLoading = false;
  currenciesLoading = false;
  adjustmentsLoading = false;
  ledgerLoading = false;
  tradesLoading = false;
  ordersLoading = false;
  alertsLoading = false;
  reconciliationLoading = false;
  withdrawalsLoading = false;
  gatewayProfilesLoading = false;
  savingPair = false;
  savingFunding = false;
  savingProfile = false;
  acknowledgingAlert = false;
  runningReconciliation = false;
  withdrawalConfigLoading = false;
  withdrawalQuoteLoading = false;
  withdrawalSubmitting = false;

  ledgerMeta: ApiMeta | null = null;
  adjustmentsMeta: ApiMeta | null = null;
  tradesMeta: ApiMeta | null = null;
  ordersMeta: ApiMeta | null = null;
  alertsMeta: ApiMeta | null = null;
  reconciliationMeta: ApiMeta | null = null;
  withdrawalsMeta: ApiMeta | null = null;

  selectedLedgerWallet: HouseWalletSummaryDto | null = null;
  ledgerPage = 1;
  adjustmentsPage = 1;
  tradesPage = 1;
  ordersPage = 1;
  alertsPage = 1;
  reconciliationPage = 1;
  withdrawalsPage = 1;
  readonly rowsPerPage = 20;

  pairFilters = { fromCurrency: '', toCurrency: '', status: '' };
  tradeFilters = { status: '', currency: '' };
  orderFilters = { status: '', currency: '' };
  alertFilters = { status: '', severity: '', currency: '' };
  reconciliationStatus = '';
  withdrawalFilters = { status: '', currency: '' };

  pairFormVisible = false;
  pairForm: HouseCurrencyPairForm = this.emptyPairForm();

  fundingFormVisible = false;
  fundingForm: CreateHouseLiquidityAdjustmentDto = this.emptyFundingForm();
  private fundingReferenceSequence = '001';

  selectedAlert: HouseLiquidityAlertDto | null = null;
  acknowledgementNote = '';

  gatewayProfileFormVisible = false;
  gatewayProfileProviderCode = '';
  gatewayProfileForm = {
    externalCustomerId: '',
    externalWalletId: '',
    verificationStatus: 'Pending',
    metadataJson: ''
  };

  withdrawalWalletId = '';
  withdrawalPaymentMethod = '';
  withdrawalAmount: number | null = null;
  withdrawalConfig: WithdrawalFormConfigResponseDto | null = null;
  withdrawalDestination: Record<string, any> = {};
  withdrawalQuote: HouseWithdrawalQuoteResponse | null = null;
  withdrawalReason = '';
  withdrawalIdempotencyKey = '';
  withdrawalAccountPin = '';

  constructor(
    private readonly house: HouseManagementService,
    private readonly api: ApiService,
    private readonly auth: AuthService,
    private readonly notify: NotificationService
  ) {}

  ngOnInit(): void {
    this.sub.add(this.auth.currentUser$.subscribe(user => {
      this.currentRole = (user?.role as UserRole | undefined) ?? null;
    }));

    this.auth.getCurrentUser().subscribe();
    this.loadOverview();
    this.loadWallets();
    this.loadAvailableCurrencies();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  get canWithdraw(): boolean {
    return this.currentRole === 'SuperAdmin';
  }

  get canMarketMaker(): boolean {
    return this.currentRole === 'SuperAdmin';
  }

  get actorLabel(): string {
    return this.currentRole || 'Finance';
  }

  setTab(tab: HouseManagementTab): void {
    this.activeTab = tab;

    switch (tab) {
      case 'overview':
        this.loadOverview();
        this.loadWallets();
        break;
      case 'liquidity':
        this.loadWallets();
        this.loadAdjustments(1);
        break;
      case 'pairs':
        this.loadPairs();
        this.loadRateProviders();
        this.loadAvailableCurrencies();
        this.loadWallets();
        break;
      case 'trades':
        this.loadTrades(1);
        break;
      case 'orders':
        this.loadOrders(1);
        break;
      case 'operations':
        this.loadAlerts(1);
        this.loadReconciliationRuns(1);
        break;
      case 'withdrawals':
        this.loadWithdrawals(1);
        this.loadGatewayProfiles();
        this.loadWallets();
        break;
    }
  }

  refreshCurrentTab(): void {
    this.setTab(this.activeTab);
  }

  loadOverview(): void {
    this.overviewLoading = true;
    this.house.getOverview().subscribe({
      next: res => {
        this.overview = res.data || null;
        if (res.data?.wallets?.length) this.wallets = res.data.wallets;
        this.overviewLoading = false;
      },
      error: err => {
        this.overviewLoading = false;
        this.showError(err, 'Unable to load the house overview.');
      }
    });
  }

  loadWallets(): void {
    this.walletsLoading = true;
    this.house.getWallets().subscribe({
      next: res => {
        this.wallets = res.data || [];
        this.walletsLoading = false;
      },
      error: err => {
        this.walletsLoading = false;
        this.showError(err, 'Unable to load house wallets.');
      }
    });
  }


  loadAvailableCurrencies(): void {
    this.currenciesLoading = true;
    this.api.getCurrencies(1, 1000).subscribe({
      next: res => {
        this.availableCurrencies = (res.data || [])
          .filter((currency: CurrencyDetailsDto) =>
            (currency.status || '').toLowerCase() === 'active'
          )
          .map((currency: CurrencyDetailsDto) => ({
            code: this.normalizeCurrency(currency.currencyCode),
            name: currency.currencyName,
            symbol: currency.symbol
          }))
          .filter(currency => !!currency.code)
          .sort((a, b) => a.code.localeCompare(b.code));

        this.currenciesLoading = false;
      },
      error: err => {
        this.availableCurrencies = this.wallets
          .map(wallet => ({
            code: this.normalizeCurrency(wallet.currency.code),
            name: wallet.currency.name,
            symbol: wallet.currency.symbol
          }))
          .filter(currency => !!currency.code)
          .sort((a, b) => a.code.localeCompare(b.code));

        this.currenciesLoading = false;
        this.showError(err, 'Unable to load the active currency catalogue. House-wallet currencies are shown instead.');
      }
    });
  }

  get pairFromCurrencyOptions(): HouseCurrencyOption[] {
    const excluded = this.normalizeCurrency(this.pairForm.toCurrency);
    return this.availableCurrencies.filter(currency => currency.code !== excluded);
  }

  get pairToCurrencyOptions(): HouseCurrencyOption[] {
    const excluded = this.normalizeCurrency(this.pairForm.fromCurrency);
    return this.availableCurrencies.filter(currency => currency.code !== excluded);
  }

  get pairFilterFromCurrencyOptions(): HouseCurrencyOption[] {
    const excluded = this.normalizeCurrency(this.pairFilters.toCurrency);
    return this.availableCurrencies.filter(currency => currency.code !== excluded);
  }

  get pairFilterToCurrencyOptions(): HouseCurrencyOption[] {
    const excluded = this.normalizeCurrency(this.pairFilters.fromCurrency);
    return this.availableCurrencies.filter(currency => currency.code !== excluded);
  }

  onPairFromCurrencyChanged(): void {
    if (
      this.normalizeCurrency(this.pairForm.fromCurrency) ===
      this.normalizeCurrency(this.pairForm.toCurrency)
    ) {
      this.pairForm.toCurrency = '';
    }
  }

  onPairToCurrencyChanged(): void {
    if (
      this.normalizeCurrency(this.pairForm.toCurrency) ===
      this.normalizeCurrency(this.pairForm.fromCurrency)
    ) {
      this.pairForm.fromCurrency = '';
    }
  }

  onPairFilterFromCurrencyChanged(): void {
    if (
      this.normalizeCurrency(this.pairFilters.fromCurrency) ===
      this.normalizeCurrency(this.pairFilters.toCurrency)
    ) {
      this.pairFilters.toCurrency = '';
    }
  }

  onPairFilterToCurrencyChanged(): void {
    if (
      this.normalizeCurrency(this.pairFilters.toCurrency) ===
      this.normalizeCurrency(this.pairFilters.fromCurrency)
    ) {
      this.pairFilters.fromCurrency = '';
    }
  }

  openFunding(wallet?: HouseWalletSummaryDto): void {
    this.fundingForm = this.emptyFundingForm();
    this.fundingForm.walletId = wallet?.walletId || '';
    this.fundingReferenceSequence = this.randomThreeDigitSequence();
    this.fundingFormVisible = true;
  }

  closeFunding(): void {
    this.fundingFormVisible = false;
    this.fundingForm = this.emptyFundingForm();
  }

  submitFunding(): void {
    const amount = Number(this.fundingForm.amount || 0);
    const externalReference =
      this.fundingForm.externalReference.trim() || this.generateFundingExternalReference();
    const reason = this.fundingForm.reason.trim() || this.fundingReasonPlaceholder;

    if (!this.fundingForm.walletId || amount <= 0) {
      this.notify.showWarning(
        'Select a wallet and enter a positive amount.',
        'Funding validation'
      );
      return;
    }

    this.fundingForm.externalReference = externalReference;
    this.fundingForm.reason = reason;

    this.savingFunding = true;
    this.house.createLiquidityAdjustment({
      ...this.fundingForm,
      amount,
      externalReference,
      reason
    }).subscribe({
      next: res => {
        this.savingFunding = false;
        this.notify.showSuccess(res.message || 'House liquidity credited.', 'House liquidity');
        this.closeFunding();
        this.loadWallets();
        this.loadAdjustments(1);
        this.loadOverview();
      },
      error: err => {
        this.savingFunding = false;
        this.showError(err, 'Unable to credit house liquidity.');
      }
    });
  }


  get fundingExternalReferencePlaceholder(): string {
    const currency = this.selectedFundingCurrencyCode || 'CAD';
    return `BANK-FUNDING-${currency}-${this.compactDate(new Date())}-${this.fundingReferenceSequence}`;
  }

  get fundingReasonPlaceholder(): string {
    return this.fundingForm.type === 'CreditCorrection'
      ? 'House liquidity credit correction'
      : 'Initial operating liquidity';
  }

  private get selectedFundingCurrencyCode(): string {
    return this.normalizeCurrency(
      this.wallets.find(wallet => wallet.walletId === this.fundingForm.walletId)?.currency.code
    );
  }

  private generateFundingExternalReference(): string {
    const existingReferences = new Set(
      this.adjustments.map(adjustment => adjustment.externalReference?.toUpperCase())
    );

    let candidate = this.fundingExternalReferencePlaceholder;
    for (let attempt = 0; attempt < 1000; attempt++) {
      if (!existingReferences.has(candidate.toUpperCase())) return candidate;
      this.fundingReferenceSequence = this.randomThreeDigitSequence();
      candidate = this.fundingExternalReferencePlaceholder;
    }

    return candidate;
  }

  private compactDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }

  private randomThreeDigitSequence(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const values = new Uint32Array(1);
      crypto.getRandomValues(values);
      return String((values[0] % 999) + 1).padStart(3, '0');
    }

    return String(Math.floor(Math.random() * 999) + 1).padStart(3, '0');
  }

  loadAdjustments(page = this.adjustmentsPage): void {
    this.adjustmentsPage = page;
    this.adjustmentsLoading = true;
    this.house.getLiquidityAdjustments(undefined, page, this.rowsPerPage).subscribe({
      next: res => {
        this.adjustments = res.data || [];
        this.adjustmentsMeta = res.meta || null;
        this.adjustmentsLoading = false;
      },
      error: err => {
        this.adjustmentsLoading = false;
        this.showError(err, 'Unable to load liquidity history.');
      }
    });
  }

  viewLedger(wallet: HouseWalletSummaryDto, page = 1): void {
    this.selectedLedgerWallet = wallet;
    this.ledgerPage = page;
    this.ledgerLoading = true;
    this.house.getWalletLedger(wallet.walletId, page, this.rowsPerPage).subscribe({
      next: res => {
        this.ledgerEntries = res.data || [];
        this.ledgerMeta = res.meta || null;
        this.ledgerLoading = false;
      },
      error: err => {
        this.ledgerLoading = false;
        this.showError(err, 'Unable to load the wallet ledger.');
      }
    });
  }

  closeLedger(): void {
    this.selectedLedgerWallet = null;
    this.ledgerEntries = [];
    this.ledgerMeta = null;
  }

  loadPairs(): void {
    this.pairsLoading = true;
    this.house.getCurrencyPairs({
      fromCurrency: this.normalizeCurrency(this.pairFilters.fromCurrency) || undefined,
      toCurrency: this.normalizeCurrency(this.pairFilters.toCurrency) || undefined,
      status: this.pairFilters.status || undefined
    }).subscribe({
      next: res => {
        this.pairs = res.data || [];
        this.pairsLoading = false;
      },
      error: err => {
        this.pairsLoading = false;
        this.showError(err, 'Unable to load house currency pairs.');
      }
    });
  }

  resetPairFilters(): void {
    this.pairFilters = { fromCurrency: '', toCurrency: '', status: '' };
    this.loadPairs();
  }

  loadRateProviders(): void {
    this.house.getRateProviders().subscribe({
      next: res => this.rateProviders = res.data || [],
      error: err => this.showError(err, 'Unable to load exchange-rate providers.')
    });
  }

  openCreatePair(): void {
    this.pairForm = this.emptyPairForm();
    this.pairFormVisible = true;
  }

  editPair(pair: HouseCurrencyPairDto): void {
    this.pairForm = {
      id: pair.id,
      fromCurrency: pair.fromCurrency,
      toCurrency: pair.toCurrency,
      markupBasisPoints: pair.markupBasisPoints,
      minimumInstantAmount: pair.minimumInstantAmount ?? null,
      maximumInstantAmount: pair.maximumInstantAmount ?? null,
      minimumHouseLiquidityBuffer: pair.minimumHouseLiquidityBuffer,
      quoteTtlSeconds: pair.quoteTtlSeconds,
      rateProviderCode: pair.rateProviderCode || 'MANUAL',
      allowManualFallback: pair.allowManualFallback,
      manualReferenceRate: pair.manualReferenceRate ?? null,
      status: pair.status
    };
    this.pairFormVisible = true;
  }

  closePairForm(): void {
    this.pairFormVisible = false;
    this.pairForm = this.emptyPairForm();
  }

  get pairUsesManualRate(): boolean {
    return !this.pairForm.rateProviderCode || this.pairForm.rateProviderCode === 'MANUAL';
  }

  get pairAllowsFallback(): boolean {
    return !this.pairUsesManualRate && this.pairForm.allowManualFallback;
  }

  onRateProviderChanged(): void {
    if (this.pairUsesManualRate) {
      this.pairForm.allowManualFallback = false;
    }
  }

  savePair(): void {
    const fromCurrency = this.normalizeCurrency(this.pairForm.fromCurrency);
    const toCurrency = this.normalizeCurrency(this.pairForm.toCurrency);
    const markup = Number(this.pairForm.markupBasisPoints ?? 0);
    const buffer = Number(this.pairForm.minimumHouseLiquidityBuffer ?? 0);
    const ttl = Number(this.pairForm.quoteTtlSeconds ?? 60);
    const minAmount = this.nullablePositiveNumber(this.pairForm.minimumInstantAmount);
    const maxAmount = this.nullablePositiveNumber(this.pairForm.maximumInstantAmount);
    const manualRate = this.nullablePositiveNumber(this.pairForm.manualReferenceRate);
    const providerCode = this.pairUsesManualRate
      ? null
      : this.pairForm.rateProviderCode.trim().toUpperCase();

    if (!this.pairForm.id && (!fromCurrency || !toCurrency || fromCurrency === toCurrency)) {
      this.notify.showWarning('Select two different currencies.', 'Pair validation');
      return;
    }

    if (markup < 0 || buffer < 0 || ttl < 5 || ttl > 300) {
      this.notify.showWarning('Review markup, buffer, and quote expiry values.', 'Pair validation');
      return;
    }

    if (minAmount !== null && maxAmount !== null && minAmount > maxAmount) {
      this.notify.showWarning('Minimum amount cannot exceed maximum amount.', 'Pair validation');
      return;
    }

    if ((this.pairUsesManualRate || this.pairAllowsFallback) && !manualRate) {
      this.notify.showWarning('A positive manual reference rate is required.', 'Rate validation');
      return;
    }

    const common = {
      markupBasisPoints: markup,
      minimumInstantAmount: minAmount,
      maximumInstantAmount: maxAmount,
      minimumHouseLiquidityBuffer: buffer,
      quoteTtlSeconds: ttl,
      rateProviderCode: providerCode,
      allowManualFallback: providerCode ? this.pairForm.allowManualFallback : false,
      manualReferenceRate: this.pairUsesManualRate || this.pairAllowsFallback ? manualRate : null
    };

    this.savingPair = true;
    const pairId = this.pairForm.id;

    const request$ = pairId
      ? this.house.updateCurrencyPair(pairId, common as UpdateHouseCurrencyPairDto)
      : this.house.createCurrencyPair({
          ...common,
          fromCurrency,
          toCurrency,
          status: this.pairForm.status || 'Active'
        } as CreateHouseCurrencyPairDto);

    request$.subscribe({
      next: res => {
        this.savingPair = false;
        this.notify.showSuccess(res.message || 'Currency pair saved.', 'House currency pair');
        this.closePairForm();
        this.loadPairs();
        this.loadOverview();
      },
      error: err => {
        this.savingPair = false;
        this.showError(err, 'Unable to save the currency pair.');
      }
    });
  }

  togglePairStatus(pair: HouseCurrencyPairDto): void {
    const nextStatus = pair.status === 'Active' ? 'Inactive' : 'Active';
    this.house.updateCurrencyPairStatus(pair.id, nextStatus).subscribe({
      next: res => {
        this.notify.showSuccess(res.message || `Pair set to ${nextStatus}.`, 'Currency pair');
        this.loadPairs();
        this.loadOverview();
      },
      error: err => this.showError(err, 'Unable to update pair status.')
    });
  }

  loadTrades(page = this.tradesPage): void {
    this.tradesPage = page;
    this.tradesLoading = true;
    this.house.getTrades({
      status: this.tradeFilters.status || undefined,
      currency: this.normalizeCurrency(this.tradeFilters.currency) || undefined,
      page,
      rowsPerPage: this.rowsPerPage
    }).subscribe({
      next: res => {
        this.trades = res.data || [];
        this.tradesMeta = res.meta || null;
        this.tradesLoading = false;
      },
      error: err => {
        this.tradesLoading = false;
        this.showError(err, 'Unable to load instant trades.');
      }
    });
  }

  resetTradeFilters(): void {
    this.tradeFilters = { status: '', currency: '' };
    this.loadTrades(1);
  }

  loadOrders(page = this.ordersPage): void {
    this.ordersPage = page;
    this.ordersLoading = true;
    this.house.getOrders({
      status: this.orderFilters.status || undefined,
      currency: this.normalizeCurrency(this.orderFilters.currency) || undefined,
      page,
      rowsPerPage: this.rowsPerPage
    }).subscribe({
      next: res => {
        this.orders = res.data || [];
        this.ordersMeta = res.meta || null;
        this.ordersLoading = false;
      },
      error: err => {
        this.ordersLoading = false;
        this.showError(err, 'Unable to load house orders.');
      }
    });
  }

  resetOrderFilters(): void {
    this.orderFilters = { status: '', currency: '' };
    this.loadOrders(1);
  }

  loadAlerts(page = this.alertsPage): void {
    this.alertsPage = page;
    this.alertsLoading = true;
    this.house.getLiquidityAlerts({
      status: this.alertFilters.status || undefined,
      severity: this.alertFilters.severity || undefined,
      currency: this.normalizeCurrency(this.alertFilters.currency) || undefined,
      page,
      rowsPerPage: this.rowsPerPage
    }).subscribe({
      next: res => {
        this.alerts = res.data || [];
        this.alertsMeta = res.meta || null;
        this.alertsLoading = false;
      },
      error: err => {
        this.alertsLoading = false;
        this.showError(err, 'Unable to load liquidity alerts.');
      }
    });
  }

  resetAlertFilters(): void {
    this.alertFilters = { status: '', severity: '', currency: '' };
    this.loadAlerts(1);
  }

  openAcknowledgeAlert(alert: HouseLiquidityAlertDto): void {
    this.selectedAlert = alert;
    this.acknowledgementNote = '';
  }

  closeAcknowledgeAlert(): void {
    this.selectedAlert = null;
    this.acknowledgementNote = '';
  }

  acknowledgeAlert(): void {
    if (!this.selectedAlert) return;

    this.acknowledgingAlert = true;
    this.house.acknowledgeLiquidityAlert(
      this.selectedAlert.id,
      this.acknowledgementNote.trim() || null
    ).subscribe({
      next: res => {
        this.acknowledgingAlert = false;
        this.notify.showSuccess(res.message || 'Alert acknowledged.', 'Liquidity alert');
        this.closeAcknowledgeAlert();
        this.loadAlerts(this.alertsPage);
        this.loadOverview();
      },
      error: err => {
        this.acknowledgingAlert = false;
        this.showError(err, 'Unable to acknowledge the alert.');
      }
    });
  }

  loadReconciliationRuns(page = this.reconciliationPage): void {
    this.reconciliationPage = page;
    this.reconciliationLoading = true;
    this.house.getReconciliationRuns({
      status: this.reconciliationStatus || undefined,
      page,
      rowsPerPage: this.rowsPerPage
    }).subscribe({
      next: res => {
        this.reconciliationRuns = res.data || [];
        this.reconciliationMeta = res.meta || null;
        this.reconciliationLoading = false;
      },
      error: err => {
        this.reconciliationLoading = false;
        this.showError(err, 'Unable to load reconciliation runs.');
      }
    });
  }

  viewReconciliationRun(run: HouseReconciliationRunDto): void {
    this.reconciliationLoading = true;
    this.house.getReconciliationRun(run.id).subscribe({
      next: res => {
        this.selectedReconciliationRun = res.data || null;
        this.reconciliationLoading = false;
      },
      error: err => {
        this.reconciliationLoading = false;
        this.showError(err, 'Unable to load reconciliation details.');
      }
    });
  }

  closeReconciliationRun(): void {
    this.selectedReconciliationRun = null;
  }

  runReconciliation(): void {
    this.runningReconciliation = true;
    this.house.runReconciliation().subscribe({
      next: res => {
        this.runningReconciliation = false;
        this.selectedReconciliationRun = res.data || null;
        this.notify.showSuccess(res.message || 'Reconciliation completed.', 'House reconciliation');
        this.loadReconciliationRuns(1);
        this.loadOverview();
      },
      error: err => {
        this.runningReconciliation = false;
        this.showError(err, 'Unable to run reconciliation.');
      }
    });
  }

  loadWithdrawals(page = this.withdrawalsPage): void {
    this.withdrawalsPage = page;
    this.withdrawalsLoading = true;
    this.house.getWithdrawals({
      status: this.withdrawalFilters.status || undefined,
      currency: this.normalizeCurrency(this.withdrawalFilters.currency) || undefined,
      page,
      rowsPerPage: this.rowsPerPage
    }).subscribe({
      next: res => {
        this.withdrawals = res.data || [];
        this.withdrawalsMeta = res.meta || null;
        this.withdrawalsLoading = false;
      },
      error: err => {
        this.withdrawalsLoading = false;
        this.showError(err, 'Unable to load house withdrawals.');
      }
    });
  }

  resetWithdrawalFilters(): void {
    this.withdrawalFilters = { status: '', currency: '' };
    this.loadWithdrawals(1);
  }

  loadGatewayProfiles(): void {
    this.gatewayProfilesLoading = true;
    this.house.getGatewayProfiles().subscribe({
      next: res => {
        this.gatewayProfiles = res.data || [];
        this.gatewayProfilesLoading = false;
      },
      error: err => {
        this.gatewayProfilesLoading = false;
        this.showError(err, 'Unable to load house gateway profiles.');
      }
    });
  }

  openGatewayProfile(profile?: HouseGatewayCustomerProfileDto): void {
    this.gatewayProfileProviderCode = profile?.providerCode || '';
    this.gatewayProfileForm = {
      externalCustomerId: profile?.externalCustomerId || '',
      externalWalletId: profile?.externalWalletId || '',
      verificationStatus: profile?.verificationStatus || 'Pending',
      metadataJson: profile?.metadataJson || ''
    };
    this.gatewayProfileFormVisible = true;
  }

  closeGatewayProfile(): void {
    this.gatewayProfileFormVisible = false;
    this.gatewayProfileProviderCode = '';
    this.gatewayProfileForm = {
      externalCustomerId: '',
      externalWalletId: '',
      verificationStatus: 'Pending',
      metadataJson: ''
    };
  }

  saveGatewayProfile(): void {
    const providerCode = this.gatewayProfileProviderCode.trim().toUpperCase();
    const externalCustomerId = this.gatewayProfileForm.externalCustomerId.trim();

    if (!providerCode || !externalCustomerId) {
      this.notify.showWarning('Provider code and external customer ID are required.', 'Gateway profile');
      return;
    }

    if (!this.isValidJsonOrEmpty(this.gatewayProfileForm.metadataJson)) {
      this.notify.showWarning('Metadata must be valid JSON.', 'Gateway profile');
      return;
    }

    this.savingProfile = true;
    this.house.upsertGatewayProfile(providerCode, {
      externalCustomerId,
      externalWalletId: this.gatewayProfileForm.externalWalletId.trim() || null,
      verificationStatus: this.gatewayProfileForm.verificationStatus.trim() || 'Pending',
      metadataJson: this.gatewayProfileForm.metadataJson.trim() || null
    }).subscribe({
      next: res => {
        this.savingProfile = false;
        this.notify.showSuccess(res.message || 'Gateway profile saved.', 'House gateway profile');
        this.closeGatewayProfile();
        this.loadGatewayProfiles();
      },
      error: err => {
        this.savingProfile = false;
        this.showError(err, 'Unable to save the gateway profile.');
      }
    });
  }

  onWithdrawalWalletChanged(): void {
    this.withdrawalConfig = null;
    this.withdrawalQuote = null;
    this.withdrawalDestination = {};
    this.withdrawalAmount = null;
  }

  loadWithdrawalConfig(): void {
    if (!this.canWithdraw || !this.withdrawalWalletId) return;

    this.withdrawalConfigLoading = true;
    this.withdrawalQuote = null;
    this.house.getWithdrawalFormConfig(
      this.withdrawalWalletId,
      this.withdrawalPaymentMethod.trim() || null
    ).subscribe({
      next: res => {
        this.withdrawalConfig = res.data || null;
        this.withdrawalPaymentMethod = this.withdrawalConfig?.paymentMethod || this.withdrawalPaymentMethod;
        this.withdrawalDestination = {};
        (this.withdrawalConfig?.fields || []).forEach(field => {
          this.withdrawalDestination[field.fieldKey] = '';
        });
        this.withdrawalConfigLoading = false;
      },
      error: err => {
        this.withdrawalConfigLoading = false;
        this.showError(err, 'Unable to load the house withdrawal form.');
      }
    });
  }

  requestWithdrawalQuote(): void {
    const amount = Number(this.withdrawalAmount || 0);
    if (!this.canWithdraw || !this.withdrawalWalletId || amount <= 0) {
      this.notify.showWarning('Select a wallet and enter a positive amount.', 'Withdrawal quote');
      return;
    }

    this.withdrawalQuoteLoading = true;
    this.house.quoteWithdrawal(
      this.withdrawalWalletId,
      amount,
      this.withdrawalPaymentMethod.trim() || null
    ).subscribe({
      next: res => {
        this.withdrawalQuote = res.data || null;
        this.withdrawalQuoteLoading = false;
        if (!this.withdrawalConfig) this.loadWithdrawalConfig();
      },
      error: err => {
        this.withdrawalQuoteLoading = false;
        this.showError(err, 'Unable to quote the house withdrawal.');
      }
    });
  }

  initiateWithdrawal(): void {
    if (!this.canWithdraw || !this.withdrawalQuote || !this.withdrawalConfig) return;

    const missingField = this.withdrawalConfig.fields.find(field =>
      field.isRequired && !String(this.withdrawalDestination[field.fieldKey] ?? '').trim()
    );

    if (missingField) {
      this.notify.showWarning(`${missingField.label} is required.`, 'Withdrawal validation');
      return;
    }

    if (!this.withdrawalReason.trim()) {
      this.notify.showWarning('A withdrawal reason is required.', 'Withdrawal validation');
      return;
    }

    const idempotencyKey = this.withdrawalIdempotencyKey.trim() ||
      `HOUSE-WD-${this.withdrawalQuote.currencyCode}-${Date.now()}`;

    this.withdrawalSubmitting = true;
    this.house.initiateWithdrawal({
      walletId: this.withdrawalQuote.walletId,
      amount: this.withdrawalQuote.amount,
      destinationDetailsJson: JSON.stringify(this.withdrawalDestination),
      paymentMethod: this.withdrawalQuote.paymentMethod || null,
      idempotencyKey,
      reason: this.withdrawalReason.trim(),
      accountPin: this.withdrawalAccountPin.trim() || null
    }).subscribe({
      next: res => {
        this.withdrawalSubmitting = false;
        this.notify.showSuccess(
          res.message || `House withdrawal ${res.data?.externalReference || ''} submitted.`,
          'House withdrawal'
        );
        this.resetWithdrawalForm();
        this.loadWithdrawals(1);
        this.loadWallets();
        this.loadOverview();
      },
      error: err => {
        this.withdrawalSubmitting = false;
        this.showError(err, 'Unable to initiate the house withdrawal.');
      }
    });
  }

  resetWithdrawalForm(): void {
    this.withdrawalWalletId = '';
    this.withdrawalPaymentMethod = '';
    this.withdrawalAmount = null;
    this.withdrawalConfig = null;
    this.withdrawalDestination = {};
    this.withdrawalQuote = null;
    this.withdrawalReason = '';
    this.withdrawalIdempotencyKey = '';
    this.withdrawalAccountPin = '';
  }

  getFieldOptions(field: PayoutFieldRequirementDto): Array<{ value: string; label: string }> {
    if (!field.optionsJson) return [];

    try {
      const parsed = JSON.parse(field.optionsJson);
      const rawOptions = Array.isArray(parsed)
        ? parsed
        : parsed?.options || parsed?.Options || [];

      return rawOptions.map((option: any) => ({
        value: String(option?.value ?? option?.Value ?? option?.code ?? option?.Code ?? ''),
        label: String(option?.label ?? option?.Label ?? option?.name ?? option?.Name ?? option?.value ?? '')
      })).filter((option: { value: string; label: string }) => option.value);
    } catch {
      return [];
    }
  }

  getWallet(walletId: string): HouseWalletSummaryDto | undefined {
    return this.wallets.find(wallet => wallet.walletId === walletId);
  }

  getWalletCurrency(walletId: string): string {
    return this.getWallet(walletId)?.currency?.code || '';
  }

  getWalletSymbol(walletId: string): string {
    return this.getWallet(walletId)?.currency?.symbol || '';
  }

  formatAmount(value: number | null | undefined, symbol = ''): string {
    const amount = Number(value || 0);
    return `${symbol}${new Intl.NumberFormat('en-CA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 8
    }).format(amount)}`;
  }

  formatDate(value?: string | null): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-CA', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  }

  basisPointsToPercent(value: number | null | undefined): string {
    return `${(Number(value || 0) / 100).toFixed(2)}%`;
  }

  statusClass(status?: string | null): string {
    const normalized = (status || '').toLowerCase();
    if (['active', 'completed', 'matched', 'resolved', 'confirmed', 'successful'].includes(normalized)) {
      return 'text-bg-success';
    }
    if (['critical', 'failed', 'cancelled', 'deleted'].includes(normalized)) {
      return 'text-bg-danger';
    }
    if (['warning', 'partial', 'partiallymatched', 'acknowledged', 'inactive'].includes(normalized)) {
      return 'text-bg-warning text-dark';
    }
    if (['processing', 'running', 'open', 'pending', 'reserved'].includes(normalized)) {
      return 'text-bg-info text-dark';
    }
    return 'text-bg-secondary';
  }

  issueClass(severity?: string | null): string {
    const normalized = (severity || '').toLowerCase();
    if (normalized === 'critical') return 'border-danger';
    if (normalized === 'warning') return 'border-warning';
    return 'border-info';
  }

  previousPage(meta: ApiMeta | null, callback: (page: number) => void): void {
    if (!meta || meta.currentPage <= 1) return;
    callback(meta.currentPage - 1);
  }

  nextPage(meta: ApiMeta | null, callback: (page: number) => void): void {
    if (!meta || meta.currentPage >= meta.totalPages) return;
    callback(meta.currentPage + 1);
  }

  private emptyPairForm(): HouseCurrencyPairForm {
    return {
      id: null,
      fromCurrency: '',
      toCurrency: '',
      markupBasisPoints: 0,
      minimumInstantAmount: null,
      maximumInstantAmount: null,
      minimumHouseLiquidityBuffer: 0,
      quoteTtlSeconds: 60,
      rateProviderCode: 'MANUAL',
      allowManualFallback: false,
      manualReferenceRate: null,
      status: 'Active'
    };
  }

  private emptyFundingForm(): CreateHouseLiquidityAdjustmentDto {
    return {
      walletId: '',
      amount: 0,
      type: 'Funding',
      externalReference: '',
      reason: ''
    };
  }

  private normalizeCurrency(value?: string | null): string {
    return (value || '').trim().toUpperCase();
  }

  private nullablePositiveNumber(value: number | null | undefined): number | null {
    if (value === null || value === undefined || value === ('' as any)) return null;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : null;
  }

  private isValidJsonOrEmpty(value?: string | null): boolean {
    if (!value?.trim()) return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  }

  private showError(error: any, fallback: string): void {
    const message =
      error?.error?.error?.details ||
      error?.error?.error?.message ||
      error?.error?.Error?.Details ||
      error?.error?.details ||
      error?.error?.message ||
      error?.message ||
      fallback;

    this.notify.showError(message, 'House Management');
  }
}
